import _ from 'lodash'
import { removeHash, resolveDataItem } from './dataResolvingUtils'
import type { DataResolvingFunctionLibrary } from './dataResolvingTypes'
import { createSchemaManager } from './schemaManager'
import type { BaseDataItem, RefSchemas } from '@wix/thunderbolt-becky-types'

const EMPTY_OBJECT = {}

const isRefType = (id: string | BaseDataItem | undefined | Array<string | BaseDataItem>): id is string =>
	typeof id === 'string'

export const createDataResolvingFunctionLibrary = (schemas: RefSchemas): DataResolvingFunctionLibrary => {
	const schemaManager = createSchemaManager(schemas)

	/**
	 * Get all the refs that we need to resolve from this data item
	 * @param mapName The name of the map that the data item belongs to
	 * @param dataItem The dataItem to resolve
	 * @returns an object with all the ids that we need to resolve (the refs object in mergeRefs will contain the resolved data items)
	 */
	const getRefs: DataResolvingFunctionLibrary['getRefs'] = (mapName, dataItem) => {
		const refInfos = schemaManager.getSchemaFromDataItem(mapName, dataItem)

		if (!refInfos || refInfos.length === 0) {
			return EMPTY_OBJECT
		}

		return refInfos.reduce<Record<string, boolean>>((acc, refInfo) => {
			const refId: Array<string | BaseDataItem> | string | undefined = _.get(dataItem, refInfo.path)
			// If the data doesn't reference anything, there is no need to resolve anything
			if (!refId) {
				return acc
			}

			// If the data references an array of ids, we need to resolve all of them
			if (Array.isArray(refId)) {
				refId.forEach((id) => {
					if (isRefType(id)) {
						const resolvedId = removeHash(id)
						if (resolvedId) {
							acc[resolvedId] = true
						}
					}
				})
			} else if (isRefType(refId)) {
				acc[removeHash(refId)] = true
			}

			return acc
		}, {})
	}

	const mergeRefs: DataResolvingFunctionLibrary['mergeRefs'] = (mapName, dataItem, resolvedRefs) => {
		const refInfos = schemaManager.getSchemaFromDataItem(mapName, dataItem)

		// If there are no refs to resolve, we can return the original data item
		if (!refInfos || Object.keys(resolvedRefs).length === 0) {
			return dataItem
		}

		// If the object has refs in nested objects, we need to deep clone the data item to avoid mutating the original data otherwise we can just shallow clone it
		const resolvedDataItem = refInfos.some(({ path }) => path.length > 1) ? _.cloneDeep(dataItem) : { ...dataItem }
		return refInfos.reduce((acc, refInfo) => {
			const refId: Array<string | BaseDataItem> | string | undefined = _.get(dataItem, refInfo.path)
			// Read the refId or the array of refIds from the original dataItem
			if (!refId) {
				return acc
			}

			if (isRefType(refId)) {
				_.set(acc, refInfo.path, resolveDataItem(refInfo, resolvedRefs, refId))
				return acc
			}

			const resolvedArray = refId.reduce((arr, id) => {
				if (isRefType(id)) {
					const resolvedRef = resolveDataItem(refInfo, resolvedRefs, id)
					// Dont add the resolved data item if it doesn't exist
					if (resolvedRef) {
						arr.push(resolvedRef)
					}
				}

				// If the ref is already resolved in the original dataItem just use it as is
				if (typeof id === 'object') {
					arr.push(id)
				}

				return arr
			}, [] as Array<BaseDataItem | string>)
			_.set(acc, refInfo.path, resolvedArray)
			return acc
		}, resolvedDataItem)
	}

	const shouldResolveDataItems: DataResolvingFunctionLibrary['shouldResolveDataItems'] = (mapName) => {
		const mapKey = `${mapName}.json`
		return !!(
			schemas[mapKey] &&
			mapName !== 'layout_data' &&
			Object.keys(schemas[mapKey]).some((dataType) => schemaManager.getSchemaByType(mapName, dataType).length > 0)
		)
	}

	return {
		shouldResolveDataItems: _.memoize(shouldResolveDataItems),
		getRefs,
		mergeRefs,
	}
}
